import { RetailStoreData } from './../class/RetailStoreData';
import { GetStoreInfoResponseData } from './../class/GetStoreInfoResponseData';
import { environment } from './../../environments/environment';
import { GetOrderResponseData } from './../class/GetOrderResponseData';
import { ProfileService } from './profile.service';
import { OrderItemApproval } from '../class/OrderItemApproval';
import { OrderSub } from '../class/OrderSub';
import { OrderInfo } from '../class/OrderInfo';
import { catchError, shareReplay, map } from 'rxjs/operators';
import { throwError, Observable } from 'rxjs';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as _ from 'lodash/core';
import { OrderItemExceptions } from '../class/OrderItemExceptions';
import { OrderItem } from '../class/OrderItem';
import { APP_BASE_HREF } from '@angular/common';

let URL;
const CACHE_SIZE = 1;

@Injectable({
  providedIn: 'root'
})
export class SubService {
  private orderCache$: Observable<GetOrderResponseData>;
  private itemExceptionsCache$: Observable<OrderItemExceptions>;
  private storeInfoCache$: Observable<GetStoreInfoResponseData>;

  constructor(private http: HttpClient, private profile: ProfileService) {
    // = environment.apiUrl
    const isDocker = environment.isDocker;
    const apiUrl = environment.apiUrl;

    if (isDocker) {
      URL = `${APP_BASE_HREF}${apiUrl}`;
    } else {
      URL = `${apiUrl}`;
    }
  }

  /**
   * Get OrderHeader and RetailStore data
   */
  getInit(): Observable<GetOrderResponseData> {
    if (!this.orderCache$) {
      this.orderCache$ = this._getOrder().pipe(shareReplay(CACHE_SIZE));
    }

    return this.orderCache$.pipe(
      map((getOrderResponseData: GetOrderResponseData) => {
        return getOrderResponseData;
      })
    );
  }

  /**
   * Get basic order information
   */
  getOrder(): Observable<OrderInfo> {
    if (!this.orderCache$) {
      this.orderCache$ = this._getOrder().pipe(shareReplay(CACHE_SIZE));
    }

    return this.orderCache$.pipe(
      map((getOrderResponseData: GetOrderResponseData) => {
        return getOrderResponseData.orderInfo;
      })
    );
  }

  /**
   * Get RetailStore data
   */
  getRetailStore(): Observable<RetailStoreData> {
    if (!this.orderCache$) {
      this.orderCache$ = this._getOrder().pipe(shareReplay(CACHE_SIZE));
    }

    return this.orderCache$.pipe(
      map((getOrderResponseData: GetOrderResponseData) => {
        return getOrderResponseData.retailStore;
      })
    );
  }

  /**
   * Get basic order information
   */
  getStoreInfo(): Observable<Ecom.StoreInfo> {
    if (!this.storeInfoCache$) {
      this.storeInfoCache$ = this._getStoreInfo().pipe(shareReplay(CACHE_SIZE));
    }

    return this.storeInfoCache$.pipe(
      map((getStoreInfoResponseData: GetStoreInfoResponseData) => {
        return getStoreInfoResponseData.storeInfo;
      })
    );
  }

  /**
   * Get a list of all the subs on the provided order
   */
  getSubs(): Observable<Array<OrderSub>> {
    if (!this.itemExceptionsCache$) {
      this.itemExceptionsCache$ = this._getOrderItemExceptions().pipe(shareReplay(CACHE_SIZE));
    }

    return this.itemExceptionsCache$.pipe(
      map((itemExceptions: OrderItemExceptions) => {
        return itemExceptions.substitutions;
      })
    );
  }

  getCustSubs(): Observable<Array<OrderSub>> {
    if (!this.itemExceptionsCache$) {
      this.itemExceptionsCache$ = this._getOrderItemExceptions().pipe(shareReplay(CACHE_SIZE));
    }

    return this.itemExceptionsCache$.pipe(
      map((itemExceptions: OrderItemExceptions) => {
        return itemExceptions.custSubs;
      })
    );
  }

  getBackupSubs(): Observable<Array<OrderSub>> {
    if (!this.itemExceptionsCache$) {
      this.itemExceptionsCache$ = this._getOrderItemExceptions().pipe(shareReplay(CACHE_SIZE));
    }

    return this.itemExceptionsCache$.pipe(
      map((itemExceptions: OrderItemExceptions) => {
        return itemExceptions.backupSubs;
      })
    );
  }

  /**
   * Get list of remaining subs that require a response from the customer
   */
  getSubsRemaining(): Observable<Array<OrderSub>> {
    if (!this.itemExceptionsCache$) {
      this.itemExceptionsCache$ = this._getOrderItemExceptions().pipe(shareReplay(CACHE_SIZE));
    }

    return this.itemExceptionsCache$.pipe(
      map((itemExceptions) => {
        return _.reduce(
          itemExceptions.substitutions,
          (results, orderSub) => {
            if (orderSub.isSubApproved.trim().length === 0) {
              results.push(orderSub);
            }
            return results;
          },
          new Array<OrderSub>()
        );
      })
    );
  }

  getCustSubsRemaining(): Observable<Array<OrderSub>> {
    if (!this.itemExceptionsCache$) {
      this.itemExceptionsCache$ = this._getOrderItemExceptions().pipe(shareReplay(CACHE_SIZE));
    }

    return this.itemExceptionsCache$.pipe(
      map((itemExceptions) => {
        return _.reduce(
          itemExceptions.custSubs,
          (results, custSub) => {
            if (custSub.isSubApproved.trim().length === 0) {
              results.push(custSub);
            }
            return results;
          },
          new Array<OrderSub>()
        );
      })
    );
  }

  getBackupSubsRemaining(): Observable<Array<OrderSub>> {
    if (!this.itemExceptionsCache$) {
      this.itemExceptionsCache$ = this._getOrderItemExceptions().pipe(shareReplay(CACHE_SIZE));
    }

    return this.itemExceptionsCache$.pipe(
      map((itemExceptions) => {
        return _.reduce(
          itemExceptions.backupSubs,
          (results, backupSub) => {
            if (backupSub.isSubApproved.trim().length === 0) {
              results.push(backupSub);
            }
            return results;
          },
          new Array<OrderSub>()
        );
      })
    );
  }

  getOutOfStockItems(): Observable<Array<OrderItem>> {
    if (!this.itemExceptionsCache$) {
      this.itemExceptionsCache$ = this._getOrderItemExceptions().pipe(shareReplay(CACHE_SIZE));
    }

    return this.itemExceptionsCache$.pipe(
      map((itemExceptions: OrderItemExceptions) => {
        return itemExceptions.oosItems;
      })
    );
  }

  getAddonItems(): Observable<Array<OrderItem>> {
    if (!this.itemExceptionsCache$) {
      this.itemExceptionsCache$ = this._getOrderItemExceptions().pipe(shareReplay(CACHE_SIZE));
    }

    return this.itemExceptionsCache$.pipe(
      map((itemExceptions: OrderItemExceptions) => {
        return itemExceptions.addonItems;
      })
    );
  }

  /**
   * returns HttpClient pre-configuresoptions
   */
  private getOptions() {
    const httpHeaders = {
      'Content-Type': 'application/json'
    };

    const httpParams = {
      orderHash: this.profile.getOrderHash()
    };

    const httpOptions = {
      headers: httpHeaders,
      params: httpParams
    };

    return httpOptions;
  }

  /**
   * POST sub approval changes
   */
  public postSubApprovals(changes: Array<OrderItemApproval>) {
    return this.http.post<Array<OrderItemApproval>>(`${URL}/subs`, changes, this.getOptions()).pipe(
      map((response: any) => {
        if (!response.isValid) {
          return throwError(response.errorMsg);
        }
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Calls get subs API
   */
  private _getOrderItemExceptions(): Observable<OrderItemExceptions> {
    return this.http.get<OrderItemExceptions>(`${URL}/subs`, this.getOptions()).pipe(
      map((response: any) => {
        if (!response.isValid) {
          return throwError(response.errorMsg);
        }
        return response.returnData;
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Call get order API
   */
  private _getOrder(): Observable<GetOrderResponseData> {
    return this.http.get<OrderInfo>(`${URL}`, this.getOptions()).pipe(
      map((response: any) => {
        if (!response.isValid) {
          return throwError(response.errorMsg);
        }
        return response.returnData;
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Call get storeInfop API
   */
  private _getStoreInfo(): Observable<GetStoreInfoResponseData> {
    return this.http.get<OrderInfo>(`${URL}/storeInfo`, this.getOptions()).pipe(
      map((response: any) => {
        if (!response.isValid) {
          return throwError(response.errorMsg);
        }
        return response.returnData;
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Handle network response errors
   */
  private handleError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      console.error(`Backend returned code ${error.status}, ` + `body was: ${error.error}`);
    }
    // return an observable with a user-facing error message
    return throwError(error);
  }
}
